import { Reflect } from "@workcanvas/reflect/client";
import { ObservableController } from "elements/base/observable";
import { applyPatches, Patch, produce, produceWithPatches } from "immer";
import { PatchInfo } from "shared/datamodel/canvas-element";
import { M } from "shared/datamodel/mutators";
import { CanvasElement } from "shared/datamodel/schemas";
import { SequenceMap, MultiElementPatch, ExtendedMultiElementPatch } from "utils/undo-redo/undo-redo-types";

export interface Action {
  do: () => MultiElementPatch[] | void;
  undo: () => MultiElementPatch[] | void;
}

const log = (...args: any[]) => {
  console.log("[UndoRedo]", ...args);
};

export default class UndoRedoStack extends ObservableController {
  private undoStack: Action[] = [];
  private redoStack: Action[] = [];
  private currentSequence: Record<string, SequenceMap> = {};

  constructor(private reflect?: Reflect<M>) {
    super();
  }

  setReflect(reflect: Reflect<M>) {
    this.reflect = reflect;
  }

  // Add a single reversible action
  addAction(action: Action, execute: boolean = true, notify: boolean = true): void {
    if (execute) {
      action.do();
    }
    this.undoStack.push(action);
    this.redoStack = []; // Clear redo stack when a new action is added
    if (notify) {
      this.notify();
    }
  }

  // Start a sequence of actions
  startSequence(sequenceId: string = "main"): void {
    if (!this.currentSequence[sequenceId]) {
      this.currentSequence[sequenceId] = {};
    }
  }

  async patchElementsWithNoSequence<T extends CanvasElement>(ids: string[], mutate: (element: T) => void) {
    const patches: Record<string, MultiElementPatch> = {};
    const fetchedElements: T[] = [];

    // Process all elements in parallel
    await Promise.all(
      ids.map(async (id) => {
        const element = (await this.reflect?.mutate.getElement(id)) as T | null;
        if (!element) {
          return null;
        }

        const [newElement, patch, inverse] = produceWithPatches(element, mutate);
        if (patch.length === 0) {
          // No changes
          return null;
        }

        patches[id] = { id, patch, inverse, newElement };
        fetchedElements.push(newElement);
      })
    );

    // Filter out null results and build patches object
    if (Object.keys(patches).length === 0) {
      return;
    }

    this.reflect?.mutate.patchCanvasEl({
      changes: Object.values(patches).map(({ id, patch }) => ({ id, patch })),
      fetchedElements,
    });

    this.addAction(
      {
        do: () => {
          this.wrappedPatchCanvasEl(this.reflect, {
            changes: Object.values(patches).map(({ id, patch }) => ({ id, patch })),
          });
          return Object.values(patches).flat();
        },
        undo: () => {
          this.wrappedPatchCanvasEl(this.reflect, {
            changes: Object.values(patches).map(({ id, inverse }) => ({ id, patch: inverse })),
          });
          return Object.values(patches).flat();
        },
      },
      false,
      true
    );

    return Object.values(patches).reduce((acc, { id, newElement }) => {
      acc[id] = newElement;
      return acc;
    }, {} as Record<string, T>);
  }

  async wrappedPatchCanvasEl(
    reflect: Reflect<M> | undefined,
    args: { changes: PatchInfo[]; fetchedElements?: CanvasElement[] }
  ) {
    await reflect?.mutate.patchCanvasEl(args);
  }

  async patchElementWithNoSequence<T extends CanvasElement>(id: string, mutate: (element: T) => void) {
    const result = await this.patchElementsWithNoSequence([id], mutate);
    return result?.[id];
  }

  async patchElements<T extends CanvasElement>(
    ids: string[],
    mutate: (element: T) => void,
    sequenceId: string = "main"
  ) {
    if (!this.currentSequence[sequenceId]) {
      return this.patchElementsWithNoSequence(ids, mutate);
    }
    const patches: Record<string, ExtendedMultiElementPatch> = {};
    for (const id of ids) {
      const elementSequence = this.currentSequence[sequenceId][id];
      let element: T | null;
      // get the previous element if it's in the sequence
      // otherwise, get the element from the reflect
      if (elementSequence && elementSequence.length > 0) {
        element = elementSequence[elementSequence.length - 1].newElement as T;
      } else {
        element = (await this.reflect?.mutate.getElement(id)) as T | null;
        this.currentSequence[sequenceId][id] = [];
      }
      if (!element) {
        continue;
      }
      const [newElement, patch, inverse] = produceWithPatches(element, mutate);
      if (patch.length === 0) {
        // No changes
        continue;
      }
      this.currentSequence[sequenceId][id].push({ patch, inverse, newElement, element });
      patches[id] = { newElement, patch, id, inverse, element };
    }
    if (Object.keys(patches).length === 0) {
      // No changes on any element
      return undefined;
    }
    await this.reflect?.mutate.patchCanvasEl({
      changes: Object.values(patches).map(({ id, patch }) => ({ id, patch })),
    });
    return Object.values(patches).reduce((acc, { id, newElement }) => {
      acc[id] = newElement;
      return acc;
    }, {} as Record<string, T>);
  }

  async patchElement<T extends CanvasElement>(id: string, mutate: (element: T) => void, sequenceId: string = "main") {
    const result = await this.patchElements([id], mutate, sequenceId);
    return result?.[id];
  }

  async patchElementWithoutUndo<T extends CanvasElement>(id: string, mutate: (element: T) => void) {
    const element = (await this.reflect?.mutate.getElement(id)) as T | null;
    if (!element) {
      return;
    }
    const [newElement, patch] = produceWithPatches(element, mutate);
    if (patch.length === 0) {
      // No changes
      return;
    }
    await this.reflect?.mutate.patchCanvasEl({ changes: [{ id, patch }] });
    return newElement;
  }

  // End a sequence of actions
  endSequence(sequenceId: string = "main"): void {
    const doPatches = Object.entries(this.currentSequence?.[sequenceId] ?? {})
      .map(([id, sequence]) => {
        if (sequence.length === 0) {
          return { id, patch: [] };
        }
        const element = sequence[0].element;
        const allPatches = sequence.map(({ patch }) => patch).flat();
        let patches: Patch[] = [];
        produce(
          element,
          (draft) => {
            applyPatches(draft, allPatches);
          },
          (p) => (patches = p)
        );
        return { id, patch: patches };
      })
      .filter(({ patch }) => patch.length > 0);
    const undoPatches = Object.entries(this.currentSequence?.[sequenceId] ?? {})
      .map(([id, sequence]) => {
        if (sequence.length === 0) {
          return { id, patch: [] };
        }
        const element = sequence[sequence.length - 1].newElement;
        const allPatches = sequence
          .reverse()
          .map(({ inverse }) => inverse)
          .flat();
        let patches: Patch[] = [];
        produce(
          element,
          (draft) => {
            applyPatches(draft, allPatches);
          },
          (p) => (patches = p)
        );
        return { id, patch: patches };
      })
      .filter(({ patch }) => patch.length > 0);

    delete this.currentSequence[sequenceId];
    if (doPatches.length === 0) {
      return;
    }

    const actions: Action = {
      do: () => {
        this.wrappedPatchCanvasEl(this.reflect, { changes: doPatches });
        return doPatches.map(({ id, patch }) => ({ id, patch, inverse: [], newElement: {} }));
      },
      undo: () => {
        this.wrappedPatchCanvasEl(this.reflect, { changes: undoPatches });
        return undoPatches.map(({ id, patch }) => ({ id, patch, inverse: [], newElement: {} }));
      },
    };
    this.addAction(actions, false);
  }

  // Execute undo
  undo(): void {
    const action = this.undoStack.pop();
    if (action) {
      log("Undoing action", action);
      const result = action.undo();
      this.redoStack.push(action);
      this.notify(undefined, { type: "undo", data: result });
    } else {
      log("Nothing to undo");
    }
  }

  // Execute redo
  redo(): void {
    const action = this.redoStack.pop();
    if (action) {
      const result = action.do();
      this.undoStack.push(action);
      this.notify(undefined, { type: "redo", data: result });
    } else {
      log("Nothing to redo");
    }
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}
